import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit3, Trash2, Eye, EyeOff, Star, 
  Package, Image, Save, X, ChevronDown, ChevronUp, Edit 
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import AdminLayout from './AdminLayout';
import FileUpload from './FileUpload';
import { getImageUrlWithFallback } from '../../utils/imageUtils';

const AdminCollections = () => {
  const { toast } = useToast();
  const { adminAuthToken } = useAdminAuth();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingCollection, setEditingCollection] = useState(null);
  const [expandedCollections, setExpandedCollections] = useState(new Set());

  // Form states
  const [collectionForm, setCollectionForm] = useState({
    name: '',
    title: '',
    subtitle: '',
    description: '',
    bannerImage: '',
    iconImage: '',
    isActive: true,
    isFeatured: false,
    isSamplesCollection: false,
    sortOrder: 0,
    metaTitle: '',
    metaDescription: ''
  });

  const [productForm, setProductForm] = useState({
    name: '',
    category: 'CREMA',
    alcoholPercentage: 17.0,
    volume: 500,
    basePrice: 35.0,
    shortDescription: '',
    fullDescription: '',
    tastingNotes: '',
    mainImage: '',
    galleryImages: [],
    baseProductType: 'amaro',
    ingredients: [],
    bottleType: '',
    designIncluded: false,
    isActive: true,
    isFeatured: false,
    sortOrder: 0
  });

  const categoryOptions = [
    'CREMA', 'FRUTTA', 'AMARO', 'GIN', 'LIQUORE', 'DISTILLATO', 'STAGIONALE'
  ];

  const productTypeOptions = [
    'amaro', 'gin', 'limoncello', 'liquore', 'distillato', 'crema'
  ];

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = adminAuthToken;

      const response = await fetch(`${backendUrl}/api/collections/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load collections');
      }

      const data = await response.json();
      if (data.success) {
        setCollections(data.collections || []);
      }
    } catch (error) {
      console.error('Error loading collections:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare le collezioni",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCollection = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = adminAuthToken;

      const response = await fetch(`${backendUrl}/api/collections/admin/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(collectionForm)
      });

      if (!response.ok) {
        throw new Error('Failed to create collection');
      }

      const data = await response.json();
      if (data.success) {
        toast({
          title: "Successo",
          description: "Collezione creata con successo"
        });
        setShowCreateModal(false);
        resetCollectionForm();
        loadCollections();
      }
    } catch (error) {
      console.error('Error creating collection:', error);
      toast({
        title: "Errore",
        description: "Impossibile creare la collezione",
        variant: "destructive"
      });
    }
  };

  const handleDeleteCollection = async (collectionId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa collezione?')) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = adminAuthToken;

      const response = await fetch(`${backendUrl}/api/collections/admin/${collectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete collection');
      }

      const data = await response.json();
      if (data.success) {
        toast({
          title: "Successo",
          description: "Collezione eliminata con successo"
        });
        loadCollections();
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      toast({
        title: "Errore",
        description: "Impossibile eliminare la collezione",
        variant: "destructive"
      });
    }
  };

  const handleAddProduct = async () => {
    if (!selectedCollection) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = adminAuthToken;

      const response = await fetch(`${backendUrl}/api/collections/admin/${selectedCollection.id}/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(productForm)
      });

      if (!response.ok) {
        throw new Error('Failed to add product');
      }

      const data = await response.json();
      if (data.success) {
        toast({
          title: "Successo",
          description: "Prodotto aggiunto alla collezione con successo"
        });
        setShowProductModal(false);
        resetProductForm();
        loadCollections();
      }
    } catch (error) {
      console.error('Error adding product:', error);
      toast({
        title: "Errore",
        description: "Impossibile aggiungere il prodotto",
        variant: "destructive"
      });
    }
  };

  const handleToggleActive = async (collection) => {
    try {
      const newActiveState = !collection.isActive;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/collections/admin/${collection.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminAuthToken}`
          },
          body: JSON.stringify({
            ...collection,
            isActive: newActiveState
          })
        }
      );

      if (response.ok) {
        toast({
          title: newActiveState ? "Collezione visibile" : "Collezione nascosta",
          description: newActiveState 
            ? "La collezione è ora visibile nella lista pubblica" 
            : "La collezione è nascosta dalla lista pubblica ma accessibile via URL diretto"
        });
        loadCollections();
      }
    } catch (error) {
      console.error('Error toggling collection active state:', error);
      toast({
        title: "Errore",
        description: "Impossibile cambiare la visibilità della collezione",
        variant: "destructive"
      });
    }
  };

  const resetCollectionForm = () => {
    setCollectionForm({
      name: '',
      title: '',
      subtitle: '',
      description: '',
      bannerImage: '',
      iconImage: '',
      isActive: true,
      isFeatured: false,
      isSamplesCollection: false,
      sortOrder: 0,
      metaTitle: '',
      metaDescription: ''
    });
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      category: 'CREMA',
      alcoholPercentage: 17.0,
      volume: 500,
      basePrice: 35.0,
      shortDescription: '',
      fullDescription: '',
      tastingNotes: '',
      mainImage: '',
      galleryImages: [],
      baseProductType: 'amaro',
      ingredients: [],
      bottleType: '',
      designIncluded: false,
      isActive: true,
      isFeatured: false,
      sortOrder: 0
    });
  };

  // Collection editing functions
  const handleEditCollection = (collection) => {
    setEditingCollection(collection);
    setCollectionForm({
      name: collection.name,
      title: collection.title,
      subtitle: collection.subtitle || '',
      description: collection.description,
      bannerImage: collection.bannerImage || '',
      iconImage: collection.iconImage || '',
      isActive: collection.isActive,
      isFeatured: collection.isFeatured,
      isSamplesCollection: collection.isSamplesCollection || false,
      sortOrder: collection.sortOrder,
      metaTitle: collection.metaTitle || '',
      metaDescription: collection.metaDescription || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateCollection = async () => {
    if (!editingCollection) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = adminAuthToken;

      const response = await fetch(`${backendUrl}/api/collections/admin/${editingCollection.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(collectionForm)
      });

      if (!response.ok) {
        throw new Error('Failed to update collection');
      }

      const data = await response.json();
      if (data.success) {
        toast({
          title: "Successo",
          description: "Collezione aggiornata con successo"
        });
        setShowEditModal(false);
        setEditingCollection(null);
        resetCollectionForm();
        loadCollections();
      }
    } catch (error) {
      console.error('Error updating collection:', error);
      toast({
        title: "Errore",
        description: "Impossibile aggiornare la collezione",
        variant: "destructive"
      });
    }
  };

  // Product editing functions
  const handleEditProduct = (collection, product) => {
    setSelectedCollection(collection);
    setSelectedProduct(product);
    setProductForm({
      name: product.name,
      category: product.category,
      alcoholPercentage: product.alcoholPercentage,
      volume: product.volume,
      basePrice: product.basePrice,
      shortDescription: product.shortDescription,
      fullDescription: product.fullDescription || '',
      tastingNotes: product.tastingNotes || '',
      mainImage: product.mainImage || '',
      galleryImages: product.galleryImages || [],
      baseProductType: product.baseProductType,
      ingredients: product.ingredients || [],
      bottleType: product.bottleType || '',
      designIncluded: product.designIncluded,
      isActive: product.isActive,
      isFeatured: product.isFeatured,
      sortOrder: product.sortOrder
    });
    setShowProductModal(true);
  };

  const handleUpdateProduct = async () => {
    if (!selectedCollection || !selectedProduct) return;

    try {
      console.log('📝 Updating product with form data:', productForm);
      console.log('🖼️ Main image path:', productForm.mainImage);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = adminAuthToken;

      const response = await fetch(`${backendUrl}/api/collections/admin/${selectedCollection.id}/products/${selectedProduct.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(productForm)
      });

      if (!response.ok) {
        throw new Error('Failed to update product');
      }

      const data = await response.json();
      console.log('✅ Product update response:', data);
      
      if (data.success) {
        toast({
          title: "Successo",
          description: "Prodotto aggiornato con successo"
        });
        setShowProductModal(false);
        setSelectedProduct(null);
        resetProductForm();
        loadCollections();
      }
    } catch (error) {
      console.error('Error updating product:', error);
      toast({
        title: "Errore",
        description: "Impossibile aggiornare il prodotto",
        variant: "destructive"
      });
    }
  };

  const handleDeleteProduct = async (collection, product) => {
    if (!confirm(`Sei sicuro di voler rimuovere il prodotto "${product.name}" dalla collezione "${collection.title}"?`)) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = adminAuthToken;

      const response = await fetch(`${backendUrl}/api/collections/admin/${collection.id}/products/${product.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      const data = await response.json();
      if (data.success) {
        toast({
          title: "Successo",
          description: "Prodotto rimosso dalla collezione"
        });
        loadCollections();
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: "Errore",
        description: "Impossibile rimuovere il prodotto",
        variant: "destructive"
      });
    }
  };

  const toggleCollectionExpanded = (collectionId) => {
    const newExpanded = new Set(expandedCollections);
    if (newExpanded.has(collectionId)) {
      newExpanded.delete(collectionId);
    } else {
      newExpanded.add(collectionId);
    }
    setExpandedCollections(newExpanded);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-center mt-2 text-gray-600">Caricamento collezioni...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gestione Collezioni</h2>
            <p className="text-gray-600 mt-1">
              Crea e gestisci collezioni di prodotti preimpostati con ricette fisse
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuova Collezione
          </button>
        </div>

        {/* Collections List */}
        <div className="bg-white rounded-lg border border-gray-200">
          {collections.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna collezione</h3>
              <p className="text-gray-500 mb-4">Inizia creando la tua prima collezione</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crea Collezione
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {collections.map((collection) => (
                <div key={collection.id} className="p-6">
                  {/* Collection Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => toggleCollectionExpanded(collection.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {expandedCollections.has(collection.id) ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                      
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-semibold text-gray-900">{collection.title}</h3>
                          {collection.isFeatured && (
                            <Star className="w-4 h-4 text-yellow-500" />
                          )}
                          {!collection.isActive && (
                            <EyeOff className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{collection.name} • {collection.products?.length || 0} prodotti</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* Toggle Active/Inactive */}
                      <button
                        onClick={() => handleToggleActive(collection)}
                        className={`flex items-center px-3 py-2 text-sm rounded-lg ${
                          collection.isActive 
                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                            : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                        }`}
                        title={collection.isActive ? 'Nascondi dalla lista pubblica' : 'Mostra nella lista pubblica'}
                      >
                        {collection.isActive ? (
                          <>
                            <Eye className="w-4 h-4 mr-1" />
                            Visibile
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-4 h-4 mr-1" />
                            Nascosta
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={() => {
                          setSelectedCollection(collection);
                          resetProductForm();
                          setShowProductModal(true);
                        }}
                        className="flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Prodotto
                      </button>
                      
                      <button
                        onClick={() => handleEditCollection(collection)}
                        className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Modifica
                      </button>
                      
                      <button
                        onClick={() => handleDeleteCollection(collection.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Collection Details (Expanded) */}
                  {expandedCollections.has(collection.id) && (
                    <div className="mt-4 space-y-4">
                      <p className="text-gray-700">{collection.description}</p>
                      
                      {/* Products in Collection */}
                      {collection.products && collection.products.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium text-gray-900">Prodotti nella collezione:</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {collection.products.map((product) => (
                              <div key={product.id} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-start justify-between">
                                      <div>
                                        <h5 className="font-medium text-gray-900">{product.name}</h5>
                                        <p className="text-sm text-gray-500">{product.category}</p>
                                        <p className="text-sm text-purple-600 font-medium">€{product.basePrice}</p>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <button
                                          onClick={() => handleEditProduct(collection, product)}
                                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                          title="Modifica prodotto"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteProduct(collection, product)}
                                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                                          title="Rimuovi prodotto"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {product.alcoholPercentage}° • {product.volume}ML
                                    </div>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-600 mt-2 line-clamp-2">{product.shortDescription}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Collection Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Nuova Collezione</h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                      <input
                        type="text"
                        value={collectionForm.name}
                        onChange={(e) => setCollectionForm({...collectionForm, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="es. tradizione"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Titolo</label>
                      <input
                        type="text"
                        value={collectionForm.title}
                        onChange={(e) => setCollectionForm({...collectionForm, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="es. TRADIZIONE"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sottotitolo</label>
                    <input
                      type="text"
                      value={collectionForm.subtitle}
                      onChange={(e) => setCollectionForm({...collectionForm, subtitle: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Sottotitolo della collezione"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                    <textarea
                      value={collectionForm.description}
                      onChange={(e) => setCollectionForm({...collectionForm, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      rows="3"
                      placeholder="Descrizione della collezione"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Immagine Banner</label>
                      <FileUpload
                        category="collections"
                        itemId={collectionForm.name || 'new-collection'}
                        currentImage={collectionForm.bannerImage}
                        onImageUploaded={(imagePath) => setCollectionForm({...collectionForm, bannerImage: imagePath})}
                        className="mb-2"
                      />
                      <input
                        type="text"
                        value={collectionForm.bannerImage}
                        onChange={(e) => setCollectionForm({...collectionForm, bannerImage: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 text-sm"
                        placeholder="URL alternativo immagine banner"
                      />
                      <p className="text-xs text-gray-500 mt-1">Immagine principale della collezione (consigliato: 1200x400px)</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Immagine Icona</label>
                      <FileUpload
                        category="collections"
                        itemId={collectionForm.name || 'new-collection'}
                        currentImage={collectionForm.iconImage}
                        onImageUploaded={(imagePath) => setCollectionForm({...collectionForm, iconImage: imagePath})}
                        className="mb-2"
                      />
                      <input
                        type="text"
                        value={collectionForm.iconImage}
                        onChange={(e) => setCollectionForm({...collectionForm, iconImage: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 text-sm"
                        placeholder="URL alternativo icona collezione"
                      />
                      <p className="text-xs text-gray-500 mt-1">Icona piccola per card e navigation (consigliato: 100x100px)</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={collectionForm.isActive}
                        onChange={(e) => setCollectionForm({...collectionForm, isActive: e.target.checked})}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Attiva</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={collectionForm.isFeatured}
                        onChange={(e) => setCollectionForm({...collectionForm, isFeatured: e.target.checked})}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">In evidenza</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={collectionForm.isSamplesCollection}
                        onChange={(e) => setCollectionForm({...collectionForm, isSamplesCollection: e.target.checked})}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 font-semibold">🧪 Collezione Campioni</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={handleCreateCollection}
                    className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Crea Collezione
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Collection Modal */}
        {showEditModal && editingCollection && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Modifica Collezione: {editingCollection.title}</h3>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingCollection(null);
                      resetCollectionForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                      <input
                        type="text"
                        value={collectionForm.name}
                        onChange={(e) => setCollectionForm({...collectionForm, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="es. tradizione"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Titolo</label>
                      <input
                        type="text"
                        value={collectionForm.title}
                        onChange={(e) => setCollectionForm({...collectionForm, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="es. TRADIZIONE"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sottotitolo</label>
                    <input
                      type="text"
                      value={collectionForm.subtitle}
                      onChange={(e) => setCollectionForm({...collectionForm, subtitle: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Sottotitolo della collezione"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                    <textarea
                      value={collectionForm.description}
                      onChange={(e) => setCollectionForm({...collectionForm, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      rows="3"
                      placeholder="Descrizione della collezione"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Immagine Banner</label>
                      <FileUpload
                        category="collections"
                        itemId={collectionForm.name || 'new-collection'}
                        currentImage={collectionForm.bannerImage}
                        onImageUploaded={(imagePath) => setCollectionForm({...collectionForm, bannerImage: imagePath})}
                        className="mb-2"
                      />
                      <input
                        type="text"
                        value={collectionForm.bannerImage}
                        onChange={(e) => setCollectionForm({...collectionForm, bannerImage: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 text-sm"
                        placeholder="URL alternativo immagine banner"
                      />
                      <p className="text-xs text-gray-500 mt-1">Immagine principale della collezione (consigliato: 1200x400px)</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Immagine Icona</label>
                      <FileUpload
                        category="collections"
                        itemId={collectionForm.name || 'new-collection'}
                        currentImage={collectionForm.iconImage}
                        onImageUploaded={(imagePath) => setCollectionForm({...collectionForm, iconImage: imagePath})}
                        className="mb-2"
                      />
                      <input
                        type="text"
                        value={collectionForm.iconImage}
                        onChange={(e) => setCollectionForm({...collectionForm, iconImage: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 text-sm"
                        placeholder="URL alternativo icona collezione"
                      />
                      <p className="text-xs text-gray-500 mt-1">Icona piccola per card e navigation (consigliato: 100x100px)</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={collectionForm.isActive}
                        onChange={(e) => setCollectionForm({...collectionForm, isActive: e.target.checked})}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Attiva</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={collectionForm.isFeatured}
                        onChange={(e) => setCollectionForm({...collectionForm, isFeatured: e.target.checked})}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">In evidenza</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={collectionForm.isSamplesCollection}
                        onChange={(e) => setCollectionForm({...collectionForm, isSamplesCollection: e.target.checked})}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 font-semibold">🧪 Collezione Campioni</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingCollection(null);
                      resetCollectionForm();
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={handleUpdateCollection}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Aggiorna Collezione
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Product Modal */}
        {showProductModal && selectedCollection && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">
                    {selectedProduct ? `Modifica Prodotto: ${selectedProduct.name}` : `Aggiungi Prodotto a "${selectedCollection.title}"`}
                  </h3>
                  <button
                    onClick={() => {
                      setShowProductModal(false);
                      setSelectedProduct(null);
                      resetProductForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome Prodotto</label>
                      <input
                        type="text"
                        value={productForm.name}
                        onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        placeholder="es. Caffè"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                      <select
                        value={productForm.category}
                        onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      >
                        {categoryOptions.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Base</label>
                      <select
                        value={productForm.baseProductType}
                        onChange={(e) => setProductForm({...productForm, baseProductType: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      >
                        {productTypeOptions.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Gradazione (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={productForm.alcoholPercentage}
                        onChange={(e) => setProductForm({...productForm, alcoholPercentage: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Volume (ML)</label>
                      <input
                        type="number"
                        value={productForm.volume}
                        onChange={(e) => setProductForm({...productForm, volume: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={productForm.basePrice}
                        onChange={(e) => setProductForm({...productForm, basePrice: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione Breve</label>
                    <textarea
                      value={productForm.shortDescription}
                      onChange={(e) => setProductForm({...productForm, shortDescription: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      rows="2"
                      placeholder="Descrizione che apparirà nella card del prodotto"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione Completa</label>
                    <textarea
                      value={productForm.fullDescription}
                      onChange={(e) => setProductForm({...productForm, fullDescription: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      rows="3"
                      placeholder="Descrizione dettagliata del prodotto"
                    />
                  </div>

                  {/* Product Images Section */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">
                      Immagini Prodotto
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Immagine Principale</label>
                      <FileUpload
                        category="products"
                        itemId={productForm.name || selectedProduct?.id || 'new-product'}
                        currentImage={productForm.mainImage}
                        onImageUploaded={(imagePath) => setProductForm({...productForm, mainImage: imagePath})}
                        className="mb-2"
                      />
                      <input
                        type="text"
                        value={productForm.mainImage}
                        onChange={(e) => setProductForm({...productForm, mainImage: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 text-sm"
                        placeholder="URL alternativo immagine principale"
                      />
                      <p className="text-xs text-gray-500 mt-1">Immagine principale del prodotto (consigliato: 800x600px)</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Note di Degustazione</label>
                      <textarea
                        value={productForm.tastingNotes}
                        onChange={(e) => setProductForm({...productForm, tastingNotes: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                        rows="2"
                        placeholder="Note organolettiche e sensoriali del prodotto"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Prodotto Base</label>
                      <select
                        value={productForm.baseProductType}
                        onChange={(e) => setProductForm({...productForm, baseProductType: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="amaro">Amaro</option>
                        <option value="limoncello">Limoncello</option>
                        <option value="grappa">Grappa</option>
                        <option value="liquore">Liquore</option>
                        <option value="crema">Crema</option>
                        <option value="bitter">Bitter</option>
                        <option value="digestivo">Digestivo</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={productForm.isActive}
                        onChange={(e) => setProductForm({...productForm, isActive: e.target.checked})}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Attivo</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={productForm.isFeatured}
                        onChange={(e) => setProductForm({...productForm, isFeatured: e.target.checked})}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">In evidenza</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={productForm.designIncluded}
                        onChange={(e) => setProductForm({...productForm, designIncluded: e.target.checked})}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Design Professionale Incluso</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowProductModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={selectedProduct ? handleUpdateProduct : handleAddProduct}
                    className={`flex items-center px-4 py-2 text-white rounded-lg hover:bg-opacity-90 ${
                      selectedProduct ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {selectedProduct ? 'Aggiorna Prodotto' : 'Aggiungi Prodotto'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCollections;